import { createContext, useReducer, useContext, ReactNode } from 'react';

import { type SelectedDatasetInfo } from '@verantos-dev/types-projects';

enum DialogActionType {
  OPEN_DIALOG = 'OPEN_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
}

export enum ProjectDialogType {
  CREATE_PROJECT = 'createProject',
  DELETE_PROJECT = 'deleteProject',
  DATASET_DETAILS = 'datasetDetails',
}

type ProjectModalAction =
  | {
      type: DialogActionType.OPEN_DIALOG;
      payload: {
        dialogType: ProjectDialogType;
        itemId: string | null;
        datasetInfo?: SelectedDatasetInfo | null;
      };
    }
  | { type: DialogActionType.CLOSE_DIALOG };

type ProjectModalState = {
  activeDialog?: ProjectDialogType | null;
  itemId?: string | null;
  datasetInfo?: SelectedDatasetInfo | null;
};

const initialProjectState: ProjectModalState = {
  activeDialog: null,
  itemId: null,
  datasetInfo: null,
};

const dialogReducer = (state: ProjectModalState, action: ProjectModalAction): ProjectModalState => {
  switch (action.type) {
    case DialogActionType.OPEN_DIALOG:
      return {
        ...state,
        activeDialog: action.payload.dialogType,
        itemId: action.payload.itemId,
        datasetInfo: action.payload.datasetInfo,
      };
    case DialogActionType.CLOSE_DIALOG:
      return { ...state, activeDialog: null, itemId: null, datasetInfo: null };
    default:
      return state;
  }
};

const ProjectModalContext = createContext<{
  state: ProjectModalState;
  openDialog: (
    dialogType: ProjectDialogType,
    itemId?: string | null,
    datasetInfo?: SelectedDatasetInfo | null
  ) => void;
  closeDialog: () => void;
}>({
  state: initialProjectState,
  openDialog: () => null,
  closeDialog: () => null,
});

const ProjectModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dialogReducer, initialProjectState);

  const openDialog = (
    dialogType: ProjectDialogType,
    itemId: string | null = null,
    datasetInfo: SelectedDatasetInfo | null = null
  ) => {
    dispatch({ type: DialogActionType.OPEN_DIALOG, payload: { dialogType, itemId, datasetInfo } });
  };

  const closeDialog = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  return (
    <ProjectModalContext.Provider value={{ state, openDialog, closeDialog }}>
      {children}
    </ProjectModalContext.Provider>
  );
};

const useProjectModalState = () => {
  const { state } = useContext(ProjectModalContext);
  return state;
};

const useProjectModalDispatch = () => {
  const { openDialog, closeDialog } = useContext(ProjectModalContext);
  return { openDialog, closeDialog };
};

const useProjectModalContext = () => {
  const state = useProjectModalState();
  const { openDialog, closeDialog } = useProjectModalDispatch();
  return { ...state, openDialog, closeDialog };
};

export { ProjectModalProvider, useProjectModalContext };
